import type React from 'react';
import {useState} from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import {Add, DeleteOutline} from '@mui/icons-material';
import {errorText} from '../../hooks/notices';
import type {DriverInput} from '../../types/shouldCostTypes';

export type DriverDraft = {name: string; seriesId: number | null; unit: string; weight: number; prices: {month: string; price: number}[]};

type Props = {
    /** The driver being edited; none for a new one. */
    driver?: DriverDraft;
    /** Indices from the market data catalogue the driver can follow. */
    series: {id: number; name: string; unit: string}[];
    onSubmit: (input: DriverInput) => Promise<unknown>;
    onClose: () => void;
};

const NONE = '';
const thisMonth = () => new Date().toISOString().slice(0, 7);

/**
 * Add or edit a price driver (Should cost and Market index tabs): a name, an optional index from the market data
 * catalogue, and without an index a price per month entered by the team. Price model lines move with their driver.
 */
export const DriverDialog: React.FC<Props> = ({driver, series, onSubmit, onClose}) => {
    const [name, setName] = useState(driver?.name ?? '');
    const [seriesId, setSeriesId] = useState<number | ''>(driver?.seriesId ?? NONE);
    const [unit, setUnit] = useState(driver?.unit ?? '');
    const [weight, setWeight] = useState(String(Math.round((driver?.weight ?? 0.5) * 100)));
    const [prices, setPrices] = useState<{month: string; price: string}[]>(
        (driver?.prices ?? []).map(p => ({month: p.month.slice(0, 7), price: String(p.price)})));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const manual = seriesId === NONE;
    const setPrice = (i: number, change: Partial<{month: string; price: string}>) =>
        setPrices(prices.map((p, j) => (j === i ? {...p, ...change} : p)));
    const filled = prices.filter(p => p.month && p.price !== '');
    const ready = (!manual || name.trim()) && filled.every(p => Number(p.price) >= 0)
        && Number(weight) >= 0 && Number(weight) <= 100;

    const submit = () => {
        setBusy(true);
        setError(null);
        onSubmit({
            name: name.trim(), seriesId: manual ? null : Number(seriesId), unit: manual ? unit.trim() : '',
            weight: Number(weight) / 100,
            prices: manual ? filled.map(p => ({month: `${p.month}-01`, price: Number(p.price)})) : [],
        }).then(onClose).catch(e => setError(errorText(e))).finally(() => setBusy(false));
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="cp-driver-title">
            <DialogTitle id="cp-driver-title">{driver ? 'Edit price driver' : 'Add a price driver'}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField label="Name" size="small" value={name} onChange={e => setName(e.target.value)} autoFocus
                               placeholder="e.g. Diesel, Cleaning labour" slotProps={{htmlInput: {maxLength: 120}}}
                               helperText={manual ? 'Required for a driver without an index' : 'Optional: the index name is used when empty'}/>
                    <TextField select label="Index" size="small" value={seriesId}
                               onChange={e => setSeriesId(e.target.value === NONE ? NONE : Number(e.target.value))}
                               helperText="Follow an index from the market data catalogue, or enter the price per month yourself">
                        <MenuItem value={NONE}>None: enter prices by hand</MenuItem>
                        {series.map(s => <MenuItem key={s.id} value={s.id}>{`${s.name}${s.unit ? ` (${s.unit})` : ''}`}</MenuItem>)}
                    </TextField>
                    <TextField label="Weight in the driver mix (%)" size="small" type="number" value={weight}
                               onChange={e => setWeight(e.target.value)} slotProps={{htmlInput: {min: 0, max: 100}}}/>
                    {manual && <>
                        <TextField label="Unit" size="small" value={unit} onChange={e => setUnit(e.target.value)}
                                   placeholder="e.g. EUR/litre" slotProps={{htmlInput: {maxLength: 40}}}/>
                        <Typography variant="subtitle2">Price per month</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Lines of a price model that follow this driver move with these prices, from the model&apos;s
                            base month to the latest month entered. One month is enough to start.
                        </Typography>
                        {prices.map((p, i) => (
                            // Rows have no id until saved; the position is their identity in this form.
                            <Stack key={i} direction="row" gap={1} alignItems="center">
                                <TextField label="Month" size="small" type="month" value={p.month} sx={{flex: 1}}
                                           onChange={e => setPrice(i, {month: e.target.value})} slotProps={{inputLabel: {shrink: true}}}/>
                                <TextField label={unit ? `Price (${unit})` : 'Price'} size="small" type="number" value={p.price} sx={{flex: 1}}
                                           onChange={e => setPrice(i, {price: e.target.value})} slotProps={{htmlInput: {min: 0}}}/>
                                <IconButton aria-label="Remove month" onClick={() => setPrices(prices.filter((_, j) => j !== i))}>
                                    <DeleteOutline fontSize="small"/>
                                </IconButton>
                            </Stack>
                        ))}
                        <Button size="small" startIcon={<Add/>} sx={{alignSelf: 'flex-start'}}
                                onClick={() => setPrices([...prices, {month: thisMonth(), price: ''}])}>Add a month</Button>
                    </>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={!ready || busy}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};
